import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, Button } from "@chakra-ui/react";
import axios from "../../../axios";
import { errorToast, successToast } from "../../../utils/toast";
import { AiOutlineCheckCircle, AiOutlineUndo } from "react-icons/ai";

const PAGE_SIZE = 10;
const ENDPOINTS = {
  pending: "/institute-withdrawal-requests",
  success: "/institute-withdrawal-success-payments",
  details: "/institute-withdrawal-details",
  paid: "/institute-withdrawal-mark-payment-done",
  paidAll: "/institute-withdrawal-mark-payment-done-all",
  revert: "/institute-withdrawal-revert",
  revertSuccess: "/institute-withdrawal-revert-success",
  export: "/institute-withdrawal-export-pending",
};
const checkResponse = (data) => {
  if (data?.success === false) throw new Error(data.message || "Request failed.");
  return data;
};
const getId = (request) => request._id ?? request.id;
const REQUEST_TABS = [
  { value: "all", label: "All", activeClass: "bg-purple text-white" },
  { value: "pending", label: "Pending", activeClass: "bg-yellow-500 text-white" },
  { value: "success", label: "Success", activeClass: "bg-green-600 text-white" },
];

const extractRequests = (response) => {
  const payload = response?.result ?? response?.data ?? response;
  const requests = Array.isArray(payload)
    ? payload
    : payload?.requests ?? payload?.withdrawalRequests ?? payload?.allRequests ?? payload?.items;

  if (!Array.isArray(requests)) {
    throw new Error("Unable to read institute withdrawal requests from the response.");
  }

  const pagination = payload?.pagination ?? response?.pagination;
  const totalPages = Number(pagination?.totalPagesCount ?? pagination?.totalPages ?? 1);

  return {
    requests,
    totalPages: Number.isFinite(totalPages) ? Math.max(1, totalPages) : 1,
  };
};

const displayValue = (value) =>
  typeof value === "string" || typeof value === "number" ? value : "-";

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const amount = Number(value);
  return Number.isFinite(amount)
    ? new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(amount)
    : "-";
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString("en-IN");
};

const InstituteWithdrawalRequests = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("all");
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmation, setConfirmation] = useState(null);
  const [detailsId, setDetailsId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: ["institute-withdrawal-requests", activeTab, page],
    queryFn: async ({ signal }) => {
      const statuses = activeTab === "all" ? ["pending", "success"] : [activeTab];
      const results = await Promise.all(statuses.map(async (status) => {
        const response = await axios.get(ENDPOINTS[status], {
          params: { page, limit: activeTab === "all" ? PAGE_SIZE / 2 : PAGE_SIZE },
          signal,
        });
        const result = extractRequests(checkResponse(response.data));
        return { ...result, requests: result.requests.map((request) => ({ ...request, withdrawalStatus: status })) };
      }));
      return {
        requests: results.flatMap((result) => result.requests),
        totalPages: Math.max(...results.map((result) => result.totalPages)),
      };
    },
    retry: false,
  });

  const requests = data?.requests ?? [];
  const totalPages = data?.totalPages ?? 1;
  const pendingIds = requests.filter((request) => request.withdrawalStatus === "pending" && getId(request)).map(getId);
  const paymentMutation = useMutation({
    mutationFn: async ({ endpoint, payload }) => checkResponse((await axios.post(endpoint, payload)).data),
    onSuccess: async () => {
      setConfirmation(null);
      setSelectedIds([]);
      successToast("Withdrawal updated successfully");
      await queryClient.invalidateQueries({ queryKey: ["institute-withdrawal-requests"] });
      await queryClient.invalidateQueries({ queryKey: ["institute-withdrawal-details"] });
    },
    onError: (err) => errorToast(err.response?.data?.message || err.message || "Unable to update withdrawal"),
  });
  const detailsQuery = useQuery({
    queryKey: ["institute-withdrawal-details", detailsId],
    enabled: Boolean(detailsId),
    queryFn: async ({ signal }) => {
      const response = await axios.get(ENDPOINTS.details, { params: { paymentId: detailsId }, signal });
      const payload = checkResponse(response.data);
      const result = payload?.result ?? payload?.data ?? payload;
      return result?.request ?? result?.withdrawalRequest ?? result?.details ?? result;
    },
    retry: false,
  });
  const detail = detailsQuery.data;
  const detailInstitute = detail?.institute ?? detail?.instituteId;
  const askToUpdate = (endpoint, payload, title, message) => setConfirmation({ endpoint, payload, title, message });
  const exportPending = async () => {
    setExporting(true);
    try {
      const response = await axios.get(ENDPOINTS.export, {
        responseType: "blob",
        params: selectedIds.length ? { selectedIds: JSON.stringify(selectedIds) } : {},
      });
      if (response.data?.type?.includes("json")) {
        const payload = JSON.parse(await response.data.text());
        throw new Error(payload.message || "Unable to export pending withdrawals");
      }
      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `institute-pending-withdrawals-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      successToast("Pending withdrawals exported");
    } catch (err) {
      errorToast(err.response?.data?.message || err.message || "Unable to export pending withdrawals");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 pt-10 sm:p-6 sm:pt-10">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Institute Withdrawal Requests</h1>
          <p className="mt-1 text-sm text-gray-500">View withdrawal requests submitted by institutes.</p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="rounded-lg bg-purple px-4 py-2 font-medium text-white disabled:opacity-50"
        >
          {isFetching ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div role="group" aria-label="Filter withdrawal requests by status" className="mb-6 flex w-fit flex-wrap gap-1 rounded-lg bg-white p-1 shadow-sm">
        {REQUEST_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            aria-pressed={activeTab === tab.value}
            onClick={() => {
              setActiveTab(tab.value);
              setPage(1);
              setSelectedIds([]);
            }}
            className={`rounded-md px-6 py-2 font-medium transition-colors ${activeTab === tab.value ? tab.activeClass : "text-gray-600 hover:bg-gray-100"}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab !== "success" && (
        <div className="mb-4 flex flex-wrap gap-3">
          <button type="button" disabled={exporting || paymentMutation.isPending} onClick={exportPending} className="rounded-lg bg-blue-600 px-4 py-2 text-white disabled:opacity-50">{exporting ? "Exporting..." : "Export Pending Excel"}</button>
          {selectedIds.length > 0 && <button type="button" disabled={isFetching || paymentMutation.isPending} onClick={() => askToUpdate(ENDPOINTS.paidAll, { selected: selectedIds }, "Mark selected as paid?", `Mark ${selectedIds.length} selected pending requests as paid?`)} className="rounded-lg bg-green-600 px-4 py-2 text-white disabled:opacity-50">Mark Selected as Paid ({selectedIds.length})</button>}
        </div>
      )}

      {error ? (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          {error.response?.data?.message || error.message || "Failed to fetch institute withdrawal requests."}
          <button type="button" onClick={() => refetch()} disabled={isFetching} className="ml-3 underline disabled:opacity-50">
            Retry
          </button>
        </div>
      ) : isPending ? (
        <p role="status" className="p-8 text-center text-gray-500">Loading institute withdrawal requests...</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg bg-white shadow-sm">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-100 text-gray-600">
                <tr>
                  <th scope="col" className="whitespace-nowrap px-4 py-3">Date</th>
                  <th scope="col" className="px-4 py-3"><div className="flex items-center gap-3"><input type="checkbox" aria-label="Select all pending requests on this page" disabled={!pendingIds.length || isFetching || paymentMutation.isPending} checked={pendingIds.length > 0 && pendingIds.every((id) => selectedIds.includes(id))} onChange={(event) => setSelectedIds(event.target.checked ? pendingIds : [])} />Name</div></th>
                  {["Amount", "Bank Detail", "Action"].map((title) => (
                    <th key={title} scope="col" className="whitespace-nowrap px-4 py-3">{title}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {requests.length === 0 ? (
                  <tr><td colSpan={5} className="p-8 text-center text-gray-500">No institute withdrawal requests found.</td></tr>
                ) : requests.map((request, index) => {
                  const institute = request.institute ?? request.instituteId;
                  const bank = request.bankDetails ?? {};
                  const id = getId(request);
                  const isPendingRequest = request.withdrawalStatus === "pending";
                  const bankName = request.bankName ?? bank.bankName;
                  const accountHolder = request.accountHolderName ?? bank.accountHolderName;
                  const accountNumber = request.accountNo ?? request.accountNumber ?? bank.accountNumber ?? bank.accountNo;
                  const ifsc = request.ifscCode ?? bank.ifscCode ?? bank.ifsc;
                  const upiId = request.upiId ?? bank.upiId;

                  return (
                    <tr key={request._id ?? request.id ?? index}>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-500">{formatDate(request.createdAt ?? request.requestedAt ?? request.createdOn)}</td>
                      <td className="px-4 py-3"><div className="flex items-center gap-3">
                        {isPendingRequest && id && <input type="checkbox" aria-label={`Select request ${id}`} disabled={isFetching || paymentMutation.isPending} checked={selectedIds.includes(id)} onChange={(event) => setSelectedIds((current) => event.target.checked ? [...current, id] : current.filter((selected) => selected !== id))} />}
                        <button type="button" aria-label="Details" title="View withdrawal details" disabled={!id} onClick={() => setDetailsId(id)} className="text-left font-medium text-gray-800 hover:text-purple hover:underline disabled:opacity-50">{displayValue(institute?.name ?? request.instituteName ?? request.name ?? institute)}</button>
                      </div></td>
                      <td className="whitespace-nowrap px-4 py-3 font-semibold">{formatAmount(request.amount ?? request.withdrawalAmount)}</td>
                      <td className="px-4 py-3">
                        {bankName && <p className="font-medium text-gray-800">{displayValue(bankName)}</p>}
                        {accountHolder && <p className="text-gray-600">{displayValue(accountHolder)}</p>}
                        {accountNumber && <p><span className="text-gray-500">A/c: </span>{displayValue(accountNumber)}</p>}
                        {ifsc && <p><span className="text-gray-500">IFSC: </span>{displayValue(ifsc)}</p>}
                        {upiId && <p><span className="text-gray-500">UPI: </span>{displayValue(upiId)}</p>}
                        {!bankName && !accountHolder && !accountNumber && !ifsc && !upiId && <p className="text-gray-500">-</p>}
                      </td>
                      <td className="px-4 py-3"><div className="flex items-center gap-2">
                        <button type="button" aria-label={isPendingRequest ? "Mark as Paid" : "Paid"} title={isPendingRequest ? "Mark as Paid" : "Paid"} disabled={!isPendingRequest || !id || isFetching || paymentMutation.isPending} onClick={() => askToUpdate(ENDPOINTS.paid, { paymentId: id }, "Mark as paid?", "Confirm that payment for this withdrawal has been completed.")} className="rounded-lg p-2 text-green-700 hover:bg-green-50 focus-visible:ring-2 focus-visible:ring-green-600 disabled:opacity-50"><AiOutlineCheckCircle size={23} aria-hidden="true" /></button>
                        <button type="button" aria-label={isPendingRequest ? "Revert" : "Move to Pending"} title={isPendingRequest ? "Revert" : "Move to Pending"} disabled={!id || isFetching || paymentMutation.isPending} onClick={() => askToUpdate(isPendingRequest ? ENDPOINTS.revert : ENDPOINTS.revertSuccess, { paymentId: id }, isPendingRequest ? "Revert withdrawal?" : "Move to pending?", isPendingRequest ? "Revert this pending institute withdrawal request?" : "Move this successful institute payment back to pending?")} className="rounded-lg p-2 text-red-600 hover:bg-red-50 focus-visible:ring-2 focus-visible:ring-red-600 disabled:opacity-50"><AiOutlineUndo size={23} aria-hidden="true" /></button>
                      </div></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex items-center justify-end gap-3">
            <button type="button" onClick={() => { setPage((current) => current - 1); setSelectedIds([]); }} disabled={page <= 1 || isFetching || paymentMutation.isPending} className="rounded border bg-white px-4 py-2 disabled:opacity-50">Previous</button>
            <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
            <button type="button" onClick={() => { setPage((current) => current + 1); setSelectedIds([]); }} disabled={page >= totalPages || isFetching || paymentMutation.isPending} className="rounded border bg-white px-4 py-2 disabled:opacity-50">Next</button>
          </div>
        </>
      )}
      <Modal isOpen={Boolean(confirmation)} onClose={() => { if (!paymentMutation.isPending) setConfirmation(null); }} closeOnOverlayClick={!paymentMutation.isPending} closeOnEsc={!paymentMutation.isPending}>
        <ModalOverlay /><ModalContent>
          <ModalHeader>{confirmation?.title}</ModalHeader>
          {!paymentMutation.isPending && <ModalCloseButton />}
          <ModalBody>{confirmation?.message}</ModalBody>
          <ModalFooter gap={3}>
            <Button isDisabled={paymentMutation.isPending} onClick={() => setConfirmation(null)}>Cancel</Button>
            <Button colorScheme="purple" isLoading={paymentMutation.isPending} onClick={() => paymentMutation.mutate(confirmation)}>Confirm</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={Boolean(detailsId)} onClose={() => setDetailsId(null)} size="lg">
        <ModalOverlay /><ModalContent>
          <ModalHeader>Institute Withdrawal Details</ModalHeader><ModalCloseButton />
          <ModalBody pb={6}>
            {detailsQuery.isPending ? <p role="status">Loading withdrawal details...</p> : detailsQuery.error ? <div role="alert">{detailsQuery.error.response?.data?.message || detailsQuery.error.message}<Button onClick={() => detailsQuery.refetch()}>Retry details</Button></div> : (
              <dl className="space-y-3">{[
                ["Request ID", displayValue(detail?._id ?? detail?.id ?? detailsId)],
                ["Institute", displayValue(detailInstitute?.name ?? detail?.instituteName ?? detailInstitute)],
                ["Email", displayValue(detailInstitute?.email ?? detail?.email)],
                ["Mobile", displayValue(detailInstitute?.mobile ?? detail?.mobile)],
                ["Amount", formatAmount(detail?.amount ?? detail?.withdrawalAmount)],
                ["Status", displayValue(detail?.status ?? detail?.paymentStatus)],
                ["Account Holder", displayValue(detail?.accountHolderName ?? detail?.bankDetails?.accountHolderName)],
                ["Account Number / UPI", displayValue(detail?.upiId ?? detail?.accountNo ?? detail?.bankDetails?.accountNumber)],
                ["IFSC", displayValue(detail?.ifscCode ?? detail?.bankDetails?.ifscCode)],
                ["Requested At", formatDate(detail?.createdAt ?? detail?.createdOn)],
                ["Paid At", formatDate(detail?.paymentDoneOn ?? detail?.paidAt)],
              ].map(([label, value]) => <div key={label}><dt className="text-sm text-gray-500">{label}</dt><dd className="break-words font-medium">{value}</dd></div>)}</dl>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default InstituteWithdrawalRequests;
